import { useLingui } from "@lingui/react/macro";
import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { toast } from "@offstreetparking/design-system/feedback";
import { formatDuration, formatList } from "@offstreetparking/design-system/formatters";
import { formatPhoneNumber } from "@offstreetparking/design-system/forms";
import { CircleCheckIcon } from "@offstreetparking/design-system/icons/CircleCheckIcon";
import { EmailIcon } from "@offstreetparking/design-system/icons/EmailIcon";
import { TabletIcon } from "@offstreetparking/design-system/icons/TabletIcon";
import { useProductTracking } from "@offstreetparking/design-system/providers";
import { Text } from "@offstreetparking/design-system/typography";
import { PermitStatus } from "@offstreetparking/domain/value/permits";
import { useState } from "react";
import { offstreetClient } from "../../../../api/client";
import { SendEmailForm } from "../../../forms/EmailInput/SendEmailForm";
import { usePermitSentToast } from "../../../forms/EmailInput/usePermitSentToast";
import { SendSmsForm } from "../../../forms/SmsInput/SendSmsForm";
import { ResponsiveOverlay } from "../../../overlays/ResponsiveOverlay";
import { ConfirmationActionRow } from "./ConfirmationActionRow";

type ShareSectionProps = {
	publicIdentifier: string;
	isPaid: boolean;
	email?: string;
	isSmsEnabled: boolean;
	smsExpiryOffsetMinutes: number;
	phoneNumber?: string;
	companyId: number;
	permitStatus: PermitStatus;
};

type ShareView = "email" | "sms";

export const ShareSection = ({
	publicIdentifier,
	isPaid,
	email,
	isSmsEnabled,
	smsExpiryOffsetMinutes,
	phoneNumber,
	companyId,
	permitStatus,
}: ShareSectionProps) => {
	const { i18n, t } = useLingui();
	const isExpired = permitStatus === PermitStatus.EXPIRED;
	const isVoid = permitStatus === PermitStatus.VOID;
	const { capture } = useProductTracking();
	const showPermitSentToast = usePermitSentToast();
	const [isShareModalOpen, setIsShareModalOpen] = useState(false);
	const [activeView, setActiveView] = useState<ShareView>("email");
	const [emails, setEmails] = useState<string[]>(email ? [email] : []);
	const [smsPhoneNumber, setSmsPhoneNumber] = useState(phoneNumber);

	const {
		mutate: sendEmailPermitAndScheduleReminder,
		isPending: isSendingEmail,
		error: sendEmailError,
	} = offstreetClient.permit.sendEmailPermitAndScheduleReminder.useMutation();
	const {
		mutate: sendSms,
		isPending: isSendingSms,
		error: sendSmsError,
	} = offstreetClient.permit.sendSmsPermit.useMutation();

	const canSms = isSmsEnabled && !isExpired;

	const openView = (view: ShareView) => {
		setActiveView(view);
		setIsShareModalOpen(true);
	};

	const emailSubtitle = isExpired
		? isPaid
			? t`A copy of your receipt`
			: t`A copy of your permit`
		: isPaid
			? t`Receipt and reminder emails`
			: t`Confirmation and reminder emails`;

	const smsExpiryOffset = formatDuration({ duration: smsExpiryOffsetMinutes, i18n });

	if (isVoid) {
		return null;
	}

	return (
		<>
			{emails.length === 0 ? (
				<ConfirmationActionRow
					icon={EmailIcon}
					title={t({
						message: "Email notifications",
						comment: "Title of the confirmation page row for permit confirmation and reminder emails",
					})}
					subtitle={emailSubtitle}
					action={{
						label: t({
							message: "Enable",
							comment: "Link on the permit confirmation page that opens the email or text reminder form",
						}),
						onClick: () => openView("email"),
					}}
				/>
			) : (
				<ConfirmationActionRow
					isLive
					icon={CircleCheckIcon}
					iconIntent="success"
					title={formatList({ values: emails, locale: i18n.locale })}
					subtitle={emailSubtitle}
					action={{
						label: t({
							message: "Add another",
							comment:
								"Link on the permit confirmation page to send the permit to one more email address",
						}),
						onClick: () => openView("email"),
					}}
				/>
			)}

			{canSms &&
				(smsPhoneNumber ? (
					<ConfirmationActionRow
						isLive
						icon={CircleCheckIcon}
						iconIntent="success"
						title={formatPhoneNumber(smsPhoneNumber)}
						subtitle={t({
							message: `Text ${smsExpiryOffset} before expiry`,
							comment:
								"Text is the verb: an SMS is sent the given duration (e.g. 15 minutes) before the permit expires",
						})}
					/>
				) : (
					<ConfirmationActionRow
						icon={TabletIcon}
						title={t({
							message: "Text reminder",
							comment:
								"Title of the confirmation page row for the SMS reminder sent before the permit expires",
						})}
						subtitle={t({
							message: `Text ${smsExpiryOffset} before expiry`,
							comment:
								"Text is the verb: an SMS is sent the given duration (e.g. 15 minutes) before the permit expires",
						})}
						action={{
							label: t({
								message: "Enable",
								comment:
									"Link on the permit confirmation page that opens the email or text reminder form",
							}),
							onClick: () => openView("sms"),
						}}
					/>
				))}

			<ResponsiveOverlay
				isOpen={isShareModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setIsShareModalOpen(false);
					}
				}}
				onAnimationComplete={() => {
					if (!isShareModalOpen) {
						setActiveView("email");
					}
				}}
				title={
					<div className="flex items-center gap-2">
						<AccessibleIcon>
							{activeView === "sms" ? (
								<TabletIcon className="h-6 w-6" />
							) : (
								<EmailIcon className="h-6 w-6" />
							)}
						</AccessibleIcon>
						<Text weight="bold">
							{activeView === "sms"
								? t({
										message: "Text reminder",
										comment:
											"Title of the confirmation page row for the SMS reminder sent before the permit expires",
									})
								: t({
										message: "Email notifications",
										comment:
											"Title of the confirmation page row for permit confirmation and reminder emails",
									})}
						</Text>
					</div>
				}
			>
				{activeView === "email" && (
					<div>
						<Text intent="muted" size="sm" className="mb-4">
							{isPaid
								? t`Send a receipt of your permit to the following emails:`
								: t`Send a confirmation of your permit to the following emails:`}
						</Text>
						<SendEmailForm
							onSubmit={(submittedEmails) => {
								capture(
									{
										feature: "email_notifications",
										action: "send_email",
										object: "button",
										context: "confirmation",
									},
									{ publicIdentifier, companyId, emailCount: submittedEmails.length },
								);
								sendEmailPermitAndScheduleReminder(
									{ publicIdentifier, emails: submittedEmails },
									{
										onSuccess: () => {
											setEmails(submittedEmails);
											setIsShareModalOpen(false);
											showPermitSentToast(submittedEmails);
										},
									},
								);
							}}
							isLoading={isSendingEmail}
							serverError={sendEmailError}
							companyId={companyId}
						/>
					</div>
				)}

				{activeView === "sms" && canSms && (
					<SendSmsForm
						isLoading={isSendingSms}
						serverError={sendSmsError}
						onSubmit={(nextPhoneNumber) => {
							capture(
								{
									feature: "sms_notifications",
									action: "schedule_sms",
									object: "button",
									context: "confirmation",
								},
								{ companyId },
							);
							sendSms(
								{ publicIdentifier, phoneNumber: nextPhoneNumber },
								{
									onSuccess: () => {
										setSmsPhoneNumber(nextPhoneNumber);
										setIsShareModalOpen(false);
										toast({
											role: "status",
											title: t({
												message: "SMS reminder scheduled",
												comment:
													"Success toast after a parker schedules a text message reminder for their permit",
											}),
										});
									},
								},
							);
						}}
					/>
				)}
			</ResponsiveOverlay>
		</>
	);
};
